import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AdminSidebar } from './AdminSidebar';
import { Dashboard } from './Dashboard';
import { MotosPage } from './MotosPage';
import { ClientesPage } from './ClientesPage';
import { LocacoesPage } from './LocacoesPage';
import { ManutencaoPageNova as ManutencaoPage } from './ManutencaoPageNova';
import { PagamentosPage } from './PagamentosPage';
import { MotosAlugadasPage } from './MotosAlugadasPage';
import { DebitosPage } from './DebitosPage';
import { MultasPage } from './MultasPage';
import { ConfiguracoesPage } from './ConfiguracoesPage';
import { RelatorioClientesPage } from './RelatorioClientesPage';
import WhatsAppControlePageCompleta from './WhatsAppControlePageCompleta';
import { ConfiguracoesAvancadasPage } from './ConfiguracoesAvancadasPage';
import { ReceitasPageNova as ReceitasPage } from './ReceitasPageNova';
import { ReservasPage } from './ReservasPage';
import BrasilSatPage from './BrasilSatPage';
import MotosBloqueadasPage from './MotosBloqueadasPage';
import DiagnosticoPagamentosPage from './DiagnosticoPagamentosPage';
import AdminAtendimentosPage from './AdminAtendimentosPage';
import ClientesAtivosPage from './ClientesAtivosPage';
import UsuariosSistemaPage from './UsuariosSistemaPage';
import { FilaEsperaPage } from './FilaEsperaPage';
import GordinhaPage from './GordinhaPage';
import EstoquePage from './EstoquePage';
import { FechamentoMensalPage } from './FechamentoMensalPage';
import ComprovantesClientePage from './ComprovantesClientePage';
import { PesquisaGlobal } from './PesquisaGlobal';
import { AlertaManutencaoGlobal } from './AlertaManutencaoGlobal';
import { Menu, LogOut, User, Clock, Eye } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { aplicarManifestAdmin, ativarPushAdmin } from '../../hooks/useAdminPwa';
import { toast } from 'sonner';

export const AdminDashboard = () => {
  // Sidebar sempre fechado em mobile por padrão
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const { user, logout } = useAuth();

  // 🔔 PWA Admin: troca manifest com try/catch defensivo + flag de erro.
  // Se qualquer coisa falhar aqui, a página NÃO fica preta — apenas o botão
  // de notificações deixa de aparecer.
  const [pwaSuportado, setPwaSuportado] = useState(false);
  const [pushPerm, setPushPerm] = useState('unknown');
  const ehSuperAdmin = user?.tipo === 'super_admin';
  useEffect(() => {
    try {
      aplicarManifestAdmin();
      const temNotif = typeof window !== 'undefined' && 'Notification' in window;
      setPwaSuportado(temNotif);
      if (temNotif) {
        setPushPerm(Notification.permission);
      } else {
        setPushPerm('unsupported');
      }
    } catch (e) {
      // Falha silenciosa — não derrubar a página
      setPwaSuportado(false);
      setPushPerm('unsupported');
      console.warn('PWA admin init falhou:', e);
    }
  }, []);

  // 🆕 Auto-prompt em PWA instalado (mesma estratégia do ClienteApp):
  // quando o admin abre o app já instalado (standalone) e a permissão
  // ainda é 'default', dispara o prompt nativo na primeira sessão.
  useEffect(() => {
    if (!ehSuperAdmin) return;
    try {
      const isStandalone =
        window.matchMedia?.('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      const jaPediuStandalone = localStorage.getItem('admin_push_autoprompt_done');
      const navegadorSuporta =
        typeof Notification !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window;
      if (
        isStandalone &&
        !jaPediuStandalone &&
        navegadorSuporta &&
        Notification.permission === 'default'
      ) {
        setTimeout(async () => {
          try {
            const r = await ativarPushAdmin();
            localStorage.setItem('admin_push_autoprompt_done', '1');
            if (r?.ok) {
              setPushPerm('granted');
              try {
                new Notification('🔔 Notificações ativadas!', {
                  body: 'Você receberá alertas de novos comprovantes, bloqueios e mensagens.',
                  icon: '/icons/admin-icon-192.png',
                });
              } catch { /* ignore */ }
            } else {
              setPushPerm(typeof Notification !== 'undefined' ? Notification.permission : 'unknown');
            }
          } catch { /* permissão negada — usuário pode reativar via botão */ }
        }, 1200);
      }
    } catch (e) {
      console.warn('Auto-prompt push admin falhou:', e);
    }
  }, [ehSuperAdmin]);
  
  // Fechar sidebar ao redimensionar para mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Atualizar data/hora a cada minuto para o header
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000); // Atualizar a cada minuto no header

    return () => clearInterval(timer);
  }, []);

  // Função para formatar data/hora compacta para o header
  const formatarDataHoraHeader = (data) => {
    try {
      const brasilia = new Date(data.toLocaleString("en-US", {timeZone: "America/Sao_Paulo"}));
      return {
        data: brasilia.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        hora: brasilia.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      };
    } catch (error) {
      // Fallback simples
      const now = new Date();
      return {
        data: now.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        hora: now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      };
    }
  };

  return (
    <div className="flex min-h-screen lg:min-h-0 lg:h-screen lg:overflow-hidden bg-asphalt-900">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="flex-1 flex flex-col min-w-0 lg:h-full lg:overflow-hidden lg:ml-0">
        <header
          className="bg-asphalt-800/95 backdrop-blur-md border-b border-white/5 sticky top-0 z-30"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="px-3 md:px-4 py-3 md:py-4 flex items-center justify-between gap-2">
            <div className="flex items-center space-x-2 md:space-x-4 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden flex-shrink-0 relative inline-flex items-center justify-center p-3 rounded-xl bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 text-emerald-300 border border-emerald-400/40 shadow-[0_0_18px_rgba(16,185,129,0.45)] hover:from-emerald-500/40 hover:text-white hover:shadow-[0_0_22px_rgba(16,185,129,0.7)] active:scale-95 transition-all"
                aria-label="Abrir menu"
                data-testid="admin-menu-hamburger"
              >
                <Menu className="h-7 w-7" strokeWidth={2.5} />
                <span className="absolute inset-0 rounded-xl ring-2 ring-emerald-400/30 animate-pulse pointer-events-none" aria-hidden="true" />
              </button>
              <h1 className="text-sm md:text-xl lg:text-2xl font-bold text-white font-barlow tracking-tight truncate">
                Sistema Desloca
              </h1>
            </div>
            
            {/* Pesquisa Global — oculta para sub_admin (acessava dados de outros clientes) */}
            {user?.tipo !== 'sub_admin' && (
              <div className="flex-1 max-w-md mx-4">
                <PesquisaGlobal />
              </div>
            )}
            
            {/* Informações do usuário, data/hora e logout */}
            <div className="flex items-center space-x-4">
              {/* Data e Hora — visual discreto e harmonioso */}
              <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/[0.03] border border-white/5 text-[11px] text-gray-400 font-medium tracking-wide" data-testid="header-data-hora">
                <Clock className="h-3 w-3 text-emerald-400/70" />
                <span className="tabular-nums">{formatarDataHoraHeader(currentDateTime).data}</span>
                <span className="text-gray-600">·</span>
                <span className="tabular-nums text-gray-300">{formatarDataHoraHeader(currentDateTime).hora}</span>
              </div>
              
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <User className="h-4 w-4" />
                <span>Olá, {user?.username}</span>
              </div>
              {/* 🔔 Botão "Ativar notificações" — somente para super_admin
                  e quando o navegador suporta push. Sempre visível (também no mobile)
                  para que o admin consiga ativar/diagnosticar pelo celular. */}
              {ehSuperAdmin && pwaSuportado && pushPerm !== 'unsupported' && pushPerm !== 'granted' && (
                <button
                  onClick={async () => {
                    if (pushPerm === 'denied') {
                      toast.error('Notificações bloqueadas neste navegador. Toque no cadeado/ajustes do site → Permissões → Notificações → Permitir.');
                      return;
                    }
                    const r = await ativarPushAdmin();
                    if (r.ok) {
                      setPushPerm('granted');
                      toast.success('Notificações ativadas! Você será avisado de comprovantes, bloqueios e mensagens.');
                    } else if (r.reason === 'permissao_negada') {
                      toast.error('Permissão negada. Habilite manualmente nas configurações do navegador.');
                      setPushPerm(Notification.permission);
                    } else {
                      toast.error(`Não foi possível ativar (${r.reason}).`);
                    }
                  }}
                  className="flex items-center gap-1 px-2 sm:px-3 py-2 text-xs font-semibold rounded-md text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors"
                  title={pushPerm === 'denied' ? 'Notificações bloqueadas — toque para ver como reativar' : 'Ativar push notifications'}
                  data-testid="admin-ativar-push-btn"
                >
                  <span aria-hidden="true">🔔</span>
                  <span className="hidden sm:inline">{pushPerm === 'denied' ? 'Notificações bloqueadas' : 'Ativar notificações'}</span>
                  <span className="sm:hidden">{pushPerm === 'denied' ? 'Bloqueado' : 'Ativar'}</span>
                </button>
              )}
              {/* Indicador discreto quando já está ativo */}
              {ehSuperAdmin && pwaSuportado && pushPerm === 'granted' && (
                <span
                  className="hidden md:inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md text-emerald-300 border border-emerald-500/30 bg-emerald-500/5"
                  title="Push notifications ativas"
                  data-testid="admin-push-ativo-indicador"
                >
                  🔔 Ativo
                </span>
              )}
              <button
                onClick={logout}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-white/10 rounded-md transition-colors"
                title="Sair do sistema"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sair</span>
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 min-h-0 overflow-y-auto p-4 lg:p-6 pb-20 bg-asphalt-900">
          {/* Banner de Modo Visualização — aparece para todos os sub_admin */}
          {user?.tipo === 'sub_admin' && (
            <div
              data-testid="banner-modo-visualizacao"
              className="mb-4 flex items-start gap-3 rounded-xl border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-200"
            >
              <Eye className="h-5 w-5 flex-shrink-0 text-blue-400 mt-0.5" />
              <div className="leading-relaxed">
                <strong className="font-semibold text-blue-100">Modo Visualização</strong> — Você tem acesso somente leitura ao sistema.
                Para criar, editar ou excluir registros, entre em contato com o administrador principal.
              </div>
            </div>
          )}

          <div className="max-w-full">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/motos" element={<MotosPage />} />
              <Route path="/clientes" element={<ClientesPage />} />
              <Route path="/locacoes" element={<LocacoesPage />} />
              <Route path="/motos-alugadas" element={<MotosAlugadasPage />} />
              <Route path="/manutencao" element={<ManutencaoPage />} />
              <Route path="/pagamentos" element={<PagamentosPage />} />
              <Route path="/cobrancas" element={<DebitosPage />} />
              <Route path="/multas" element={<MultasPage />} />
              <Route path="/whatsapp" element={<WhatsAppControlePageCompleta />} />
              <Route path="/configuracoes" element={<ConfiguracoesPage />} />
              <Route path="/configuracoes-avancadas" element={<ConfiguracoesAvancadasPage />} />
              <Route path="/relatorio-clientes" element={<RelatorioClientesPage />} />
              <Route path="/receitas" element={<ReceitasPage />} />
              <Route path="/reservas" element={<ReservasPage />} />
              <Route path="/usuarios" element={<UsuariosSistemaPage />} />
              <Route path="/fila-espera" element={<FilaEsperaPage />} />
              <Route path="/comprovantes-app" element={<ComprovantesClientePage />} />
              {/* Rotas restritas apenas para super_admin */}
              {user?.tipo === 'super_admin' && (
                <>
                  <Route path="/gordurinha" element={<GordinhaPage />} />
                  <Route path="/estoque" element={<EstoquePage />} />
                  <Route path="/brasilsat" element={<BrasilSatPage />} />
                  <Route path="/motos-bloqueadas" element={<MotosBloqueadasPage />} />
                  <Route path="/diagnostico-pagamentos" element={<DiagnosticoPagamentosPage />} />
                  <Route path="/atendimentos" element={<AdminAtendimentosPage />} />
                  <Route path="/clientes-ativos" element={<ClientesAtivosPage />} />
                </>
              )}
              <Route
                path="/fechamento"
                element={<FechamentoMensalPage />}
              />
            </Routes>
          </div>
        </main>
      </div>
      
      {/* Alertas globais de manutenção - aparecem em qualquer página */}
      <AlertaManutencaoGlobal />
    </div>
  );
};